import React, { createContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { getEnv } from '../config/env';
import { setForceReauthCallback, resetAuthFailureCounter } from '../utils/authenticatedFetch';
import { SESSION_KEY_REGION } from '../types/AppConfig';
import type { StoredRegion } from '../types/AppConfig';
import { logger } from '../utils/logger';

interface UserInfo {
  orgId: string;
  orgFriendlyName: string;
  orgName: string;
  userName: string;
  userEmail: string;
  userFullName: string;
  // Add more fields as needed
}

interface AuthContextType {
  isAuthenticated: boolean;
  accessToken: string | null;
  userInfo: UserInfo | null;
  login: () => void;
  logout: () => void;
  loading: boolean;
  authError: string | null;
  clearAuthError: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

/** Returns the loginBaseUri and clientId for the currently selected region.
 *  Falls back to env vars if no region has been stored in sessionStorage. */
function getRegionCredentials(): { loginBaseUri: string; clientId: string } | null {
  const raw = sessionStorage.getItem(SESSION_KEY_REGION);
  if (raw) {
    try {
      const stored = JSON.parse(raw) as StoredRegion;
      if (stored.loginBaseUri && stored.clientId) {
        return { loginBaseUri: stored.loginBaseUri, clientId: stored.clientId };
      }
    } catch {
      // stored region is corrupt — user must log in again via the login page
    }
  }
  return null;
}

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [userInfo, setUserInfo] = useState<UserInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState<string | null>(null);

  const clearAuthError = () => setAuthError(null);

  // Register the force reauth callback when component mounts
  useEffect(() => {
    setForceReauthCallback(() => {
      // Clear auth state
      sessionStorage.removeItem('userData');
      sessionStorage.removeItem('genesysCloudToken');
      setAccessToken(null);
      setUserInfo(null);
      setIsAuthenticated(false);

      logger.warn('Token expired or invalid — forcing re-authentication');

      // Redirect to OAuth using the previously selected region (or env fallback)
      const creds = getRegionCredentials();
      if (!creds) {
        logger.error('Force re-auth failed — no region credentials available');
        return;
      }
      const { loginBaseUri, clientId } = creds;
      const redirectUriBase = window.location.origin;
      const queryString = new URLSearchParams({
        response_type: 'token',
        client_id: clientId,
        redirect_uri: redirectUriBase + '/landing/'
      }).toString();
      const authUrl = `${loginBaseUri}/oauth/authorize?${queryString}`;
      logger.debug('Force re-auth redirect', {
        loginBaseUri,
        redirectUri: redirectUriBase + '/landing/'
      });
      window.location.href = authUrl;
    });
  }, []);

  // Helper to get token and expiry from URL hash
  const getTokenFromHash = (): { token: string; expiresIn: number | null } | null => {
    const hash = window.location.hash;
    if (hash.includes('access_token=')) {
      const params = new URLSearchParams(hash.substring(1));
      const token = params.get('access_token');
      if (!token) return null;
      const expiresInRaw = params.get('expires_in');
      const expiresIn = expiresInRaw ? parseInt(expiresInRaw, 10) : null;
      return { token, expiresIn: expiresIn && !isNaN(expiresIn) ? expiresIn : null };
    }
    return null;
  };

  // Load user info from sessionStorage if available and not expired
  useEffect(() => {
    const userDataRaw = sessionStorage.getItem('userData');
    const token = sessionStorage.getItem('genesysCloudToken');
    if (userDataRaw && token) {
      try {
        const userData = JSON.parse(userDataRaw);
        if (userData.expiry > Date.now()) {
          setUserInfo(userData.value);
          setAccessToken(token);
          setIsAuthenticated(true);
          setLoading(false);
          // Set global logger properties with restored user context
          logger.setProperties(userData.value.userFullName, userData.value.userEmail, userData.value.orgName, userData.value.orgId);
          logger.info('Auth restored from session', {
            user: userData.value.userFullName,
            email: userData.value.userEmail,
            org: userData.value.orgName,
            orgId: userData.value.orgId,
            sessionExpiresAt: new Date(userData.expiry).toISOString()
          });
          return;
        } else {
          logger.warn('Stored session expired — clearing and re-authenticating', {
            expiredAt: new Date(userData.expiry).toISOString()
          });
          sessionStorage.removeItem('userData');
          sessionStorage.removeItem('genesysCloudToken');
        }
      } catch {
        logger.warn('Failed to parse stored session data — clearing sessionStorage');
        sessionStorage.removeItem('userData');
        sessionStorage.removeItem('genesysCloudToken');
      }
    }

    // If not in sessionStorage, check for token in URL hash
    const hashResult = getTokenFromHash();
    if (hashResult) {
      const { token: hashToken, expiresIn: hashExpiresIn } = hashResult;
      setLoading(true);
      logger.info('OAuth callback received — validating token with auth service');
      {
        /* New V2 validate endpoint */
      }
      const validateUser = async () => {
        try {
          // Get the stored region info
          const storedRegionRaw = sessionStorage.getItem(SESSION_KEY_REGION);
          let region = '';

          if (storedRegionRaw) {
            try {
              const storedRegion = JSON.parse(storedRegionRaw) as StoredRegion;
              region = storedRegion.key.replace(/_/g, '-') || '';
            } catch {
              logger.warn('Could not parse stored region');
            }
          }

          const env = getEnv();
          const baseUrl = env.VITE_EDCI_AUTH_URL;
          const url = `${baseUrl}/v2/Validate`;
          logger.debug('Sending token to auth service', { url, region });

          const response = await fetch(url, {
            headers: {
              Authorization: `Bearer ${hashToken}`,
              'Edci-Application-Name': env.VITE_APP_KEY,
              'Edci-Genesys-Region': region
            }
          });

          if (!response.ok) {
            throw new Error(
              `Authentication failed (HTTP ${response.status}). ` +
              `Please check that you selected the correct region and try again.`
            );
          }

          const respData = await response.json();

          // Log the response to debug region differences
          logger.debug('Validation response received', {
            region,
            hasUserName: !!respData.userName,
            hasUserEmail: !!respData.userEmail,
            hasOrgId: !!respData.orgId,
            hasUserFullName: !!respData.userFullName,
            hasOrgName: !!respData.orgName,
            responseKeys: Object.keys(respData)
          });

          if (
            respData.userName &&
            respData.userEmail &&
            respData.orgId &&
            respData.userFullName &&
            respData.orgName &&
            respData.orgFriendlyName
          ) {
            const userInfo: UserInfo = {
              orgId: respData.orgId,
              orgName: respData.orgName,
              orgFriendlyName: respData.orgFriendlyName,
              userName: respData.userName,
              userEmail: respData.userEmail,
              userFullName: respData.userFullName
            };
            setUserInfo(userInfo);
            setAccessToken(hashToken);
            setIsAuthenticated(true);
            setAuthError(null);
            // Use actual token expiry from OAuth, fall back to 24h if not available
            const expiresInMs = hashExpiresIn ? hashExpiresIn * 1000 : 1000 * 60 * 60 * 24;
            const userInfoWithExpiry = {
              value: userInfo,
              expiry: Date.now() + expiresInMs
            };
            sessionStorage.setItem('userData', JSON.stringify(userInfoWithExpiry));
            sessionStorage.setItem('genesysCloudToken', hashToken);
            // Clean up URL
            window.history.replaceState({}, document.title, window.location.pathname);
            // Set global logger properties with user context
            logger.setProperties(userInfo.userFullName, userInfo.userEmail, userInfo.orgName, userInfo.orgId);
            // Don't reset auth failure counter here - let it accumulate to detect loops
            logger.info('Login successful', {
              user: userInfo.userFullName,
              email: userInfo.userEmail,
              org: userInfo.orgName,
              orgId: userInfo.orgId,
              sessionExpiresAt: new Date(userInfoWithExpiry.expiry).toISOString()
            });
          } else {
            throw new Error(
              'Your account could not be verified. ' +
              'Please check that you selected the correct region and try again.'
            );
          }
        } catch (err) {
          sessionStorage.removeItem('userData');
          sessionStorage.removeItem('genesysCloudToken');
          setUserInfo(null);
          setAccessToken(null);
          setIsAuthenticated(false);

          // Clean up URL hash to prevent infinite loop on retry
          window.history.replaceState({}, document.title, window.location.pathname);

          const raw = err instanceof Error ? err.message : String(err);
          // "Failed to fetch" is the browser's generic network error — give a clearer message
          const friendly = raw.toLowerCase().includes('failed to fetch')
            ? 'Unable to reach the authentication service. Please check your connection and try again.'
            : raw;
          setAuthError(friendly);
          logger.error('Login failed', { reason: friendly });
        } finally {
          setLoading(false);
        }
      };
      validateUser();
    } else {
      setLoading(false);
    }
  }, []);

  const login = () => {
    const creds = getRegionCredentials();
    if (!creds) {
      setAuthError('No region selected. Please select a region and try again.');
      return;
    }
    const { loginBaseUri, clientId } = creds;
    const redirectUriBase = window.location.origin;
    const queryString = new URLSearchParams({
      response_type: 'token',
      client_id: clientId,
      redirect_uri: redirectUriBase + '/landing/'
    }).toString();
    const authUrl = `${loginBaseUri}/oauth/authorize?${queryString}`;
    logger.info('Redirecting to Genesys Cloud OAuth', {
      loginBaseUri,
      redirectUri: redirectUriBase + '/landing/'
    });
    window.location.href = authUrl;
  };

  const logout = () => {
    const currentUser = userInfo;
    sessionStorage.removeItem('userData');
    sessionStorage.removeItem('genesysCloudToken');
    sessionStorage.removeItem(SESSION_KEY_REGION);
    setAccessToken(null);
    setUserInfo(null);
    // Clear global logger properties on logout
    logger.clearProperties();
    // Reset auth failure counter on logout
    resetAuthFailureCounter();
    setIsAuthenticated(false);
    setAuthError(null);
    logger.info('User logged out', {
      user: currentUser?.userFullName ?? 'unknown',
      email: currentUser?.userEmail ?? 'unknown',
      org: currentUser?.orgName ?? 'unknown',
      orgFriendlyName: currentUser?.orgFriendlyName ?? 'unknown'
    });
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        accessToken,
        userInfo,
        login,
        logout,
        loading,
        authError,
        clearAuthError
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext };
